import { useHead } from '@unhead/react';
import { Col, Flex, Row, message } from 'antd';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import MaintenanceAlert from 'business/user/components/maintenanceAlert';
import aggregatedReport from 'business/user/pages/Dashboard/assets/aggregatedReport.svg';
import bi from 'business/user/pages/Dashboard/assets/bi.svg';
import performanceIndicators from 'business/user/pages/Dashboard/assets/performance-indicators.svg';
import productionAnalyze from 'business/user/pages/Dashboard/assets/production-analyse.svg';
import taskExcelExport from 'business/user/pages/Dashboard/assets/task-excel-export.svg';
import weekReport from 'business/user/pages/Dashboard/assets/weekReport.svg';
import DashboardTile from 'business/user/pages/Dashboard/components/dashboard-tile';
import styles from 'business/user/pages/Dashboard/index.module.scss';
import Routes from 'config/routes';
import {
  Module_Enum,
  useAvailableCustomIndicatorsQuery,
} from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';

export function AnalyzeShiftReportDashboardPage() {
  const { t } = useTranslation();

  useHead({ title: t('user.menu.sections.reporting.title') });

  const { currentConstructionSite } = useAppContext();
  const id = currentConstructionSite?.id;

  const gqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);

  const indicatorQuery = useAvailableCustomIndicatorsQuery({
    variables: { constructionSiteId: id },
    fetchPolicy: 'network-only',
    onError: () => {
      message.error(t('errors.error_generic'));
    },
    context: {
      headers: gqlHeaders,
    },
  });

  const { isMobile, isTablet } = useMediaType();

  const aggregatedReportCard = (
    <Col xs={24} md={12} key="aggregatedReport">
      <DashboardTile
        title={t('pages.manager.aggregatedReport.title')}
        icon={aggregatedReport}
        loading={false}
        route={Routes.ReportExportAggregated}
        ctaLabel={t('pages.manager.aggregatedReport.button')}
        disabled={isMobile && !isTablet}
        disabledText={t('pages.manager.disabledMobile')}
      />
    </Col>
  );

  const batchReportCard = (
    <Col xs={24} md={12} key="batchReport">
      <DashboardTile
        title={t('pages.manager.batchReport.title')}
        icon={weekReport}
        loading={false}
        route={Routes.ReportExportBatch}
        ctaLabel={t('pages.manager.batchReport.button')}
        disabled={isMobile && !isTablet}
        disabledText={t('pages.manager.disabledMobile')}
      />
    </Col>
  );

  const taskAnalyzerCard = (
    <Col xs={24} md={12} key="taskAnalyzer">
      <DashboardTile
        title={t('pages.manager.taskAnalyzer.title')}
        icon={bi}
        loading={false}
        route={Routes.ActivityAnalyze}
        ctaLabel={t('pages.manager.taskAnalyzer.button')}
        disabledText={t('pages.manager.disabledMobile')}
      />
    </Col>
  );

  const productionAnalyzeCard = (
    <Col xs={24} md={12} key="productionAnalyze">
      <DashboardTile
        title={t('pages.manager.productionAnalyze.title')}
        icon={productionAnalyze}
        loading={false}
        route={Routes.ProductionAnalyze}
        ctaLabel={t('pages.manager.productionAnalyze.button')}
      />
    </Col>
  );

  const indicatorsCard = indicatorQuery.data?.indicator.length ? (
    <Col xs={24} md={12} key="indicators">
      <DashboardTile
        title={t('pages.manager.indicators.title')}
        icon={performanceIndicators}
        loading={false}
        route={Routes.Indicators}
        ctaLabel={t('pages.manager.indicators.button')}
        disabledText={t('pages.manager.disabledMobile')}
      />
    </Col>
  ) : null;

  const taskExportCard = (
    <Col xs={24} md={12} key="taskExport">
      <DashboardTile
        title={t('pages.manager.taskExport.title')}
        icon={taskExcelExport}
        loading={false}
        route={Routes.TaskExport}
        ctaLabel={t('pages.manager.taskExport.button')}
        disabled={isMobile && !isTablet}
        disabledText={t('pages.manager.disabledMobile')}
      />
    </Col>
  );

  const desktopCards = [
    aggregatedReportCard,
    batchReportCard,
    taskAnalyzerCard,
    productionAnalyzeCard,
    indicatorsCard,
    taskExportCard,
  ];

  const mobileCards = [
    taskAnalyzerCard,
    productionAnalyzeCard,
    indicatorsCard,
    aggregatedReportCard,
    batchReportCard,
    taskExportCard,
  ];

  return (
    <Flex vertical align="center" className={styles.container}>
      <MaintenanceAlert />
      <Row gutter={[30, 30]} className={styles.menuContainer}>
        {(isMobile ? mobileCards : desktopCards).map((card) => card)}
      </Row>
    </Flex>
  );
}
